import type { PracticeComparison } from '@engineer-os/engine';
import { useEffect, useState } from 'react';
import { useLocale } from '../../state/useLocale';
import { intlTag } from '../../lib/format';
import { savingsCopy } from '../../lib/strings/savings';
import { fetchMeter } from '../../lib/efficiency';

export function CostPracticeGuide({ signedIn }: { signedIn: boolean }) {
  const { locale } = useLocale();
  const s = savingsCopy[locale];
  const [shown, setShown] = useState(false);
  useEffect(() => {
    let live = true;
    setShown(false);
    if (signedIn) void fetchMeter().then((m) => { if (live) setShown(m?.shown === true); });
    return () => { live = false; };
  }, [signedIn]);
  if (!shown) return null;
  return <details className="cost-practice">
    <summary>{s.practiceTitle}</summary><p>{s.practiceIntro}</p><p className="note">{s.metering}</p>
    <a href="/agent" target="_blank" rel="noopener">{s.connect}</a>
  </details>;
}

export function CostPractice({ comparison, missionId }: { comparison: PracticeComparison; missionId: string }) {
  const { locale } = useLocale();
  const s = savingsCopy[locale];
  const { before, after } = comparison;
  const usd = (micro: number) => new Intl.NumberFormat(intlTag(locale), { style: 'currency', currency: 'USD', minimumFractionDigits: 4, maximumFractionDigits: 6 }).format(micro / 1e6);
  const change = after ? (before.costMicro - after.costMicro) / before.costMicro : null;
  const percent = change === null ? '' : new Intl.NumberFormat(intlTag(locale), { style: 'percent', maximumFractionDigits: 1 }).format(Math.abs(change));
  return <section className="cost-practice" aria-label={s.comparison}>
    <h4>{s.comparison}</h4>
    <p className="note">{s.priced}</p>
    <dl><div><dt>{s.baseline}</dt><dd>{usd(before.costMicro)}</dd><small>{s.counts(before.attempts)}</small></div>
      {after && <div><dt>{s.retryTotal}</dt><dd>{usd(after.costMicro)}</dd><small>{s.counts(after.attempts)}</small></div>}</dl>
    {change === null ? <p>{s.baselineSaved}</p> : <>
      <p className="cost-practice__delta">{change === 0 ? s.equal : `${change > 0 ? s.less : s.more}: ${percent}`}</p>
      <p className="note">{s.practiceNote}</p>
    </>}
    {comparison.reported && <p className="note">{s.reported}</p>}
    <p><bdi>{comparison.book}</bdi></p>
    <a href={`/efficiency?retry=${encodeURIComponent(missionId)}#right-size-model`} target="_blank" rel="noopener">{s.replay}</a>
  </section>;
}
